/**
 * BonusSection - Bonus percentage configuration
 * Two completely separate sections: Deposit Bonus and Referee Conversion Bonus
 * Each section has its own state, handlers, and submit button - NO ENTANGLEMENT
 */

import { useState, useEffect } from 'react';
import {
  Gift,
  LayoutDashboard,
  Users,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileText,
  Check,
} from 'lucide-react';
import {
  MenuButton,
  useToast,
  Toast,
  StatsCard,
  InputBar,
  ActionButton,
  TogglePill,
} from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';

export interface BonusSectionProps {
  currentPercentage: number;
  refereeConversionPercent?: number;
  isLoading?: boolean;
  onUpdateDeposit?: (percentage: number) => Promise<void>;
  onUpdateReferee?: (percentage: number) => Promise<void>;
  onNavigate?: (page: string) => void;
}

const DEPOSIT_PRESETS = [10, 15, 20, 25, 30, 40, 45, 50, 55, 60, 70, 75, 80, 90, 100];
const REFEREE_PRESETS = [10, 15, 20, 25, 30, 40, 45, 50, 55, 60, 70, 75, 80, 90, 100];

const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'referees', label: 'Referees', icon: Users },
];

export function BonusSection({
  currentPercentage,
  refereeConversionPercent = 0,
  isLoading = false,
  onUpdateDeposit,
  onUpdateReferee,
  onNavigate,
}: BonusSectionProps) {
  const { toast, showToast, hideToast } = useToast();

  return (
    <div className="space-y-6 pb-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}

      {/* Header with menu */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Bonus Settings</h2>
        <MenuButton
          options={adminMenuOptions}
          value="bonus"
          onSelect={(page) => onNavigate?.(page)}
          sheetTitle="Navigate"
        />
      </div>

      {/* Current Settings Display */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          Current Settings
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <StatsCard
            title="Deposit Bonus"
            value={`${currentPercentage}%`}
            subtitle="Applied to deposits"
            icon={Gift}
            variant="info"
            loading={isLoading}
          />
          <StatsCard
            title="Referee Bonus"
            value={`${refereeConversionPercent}%`}
            subtitle="Token conversions"
            icon={Users}
            variant="success"
            loading={isLoading}
          />
        </div>
      </section>

      {/* ========== DEPOSIT BONUS SECTION - COMPLETELY INDEPENDENT ========== */}
      <DepositBonusEditor
        currentValue={currentPercentage}
        isLoading={isLoading}
        onSubmit={onUpdateDeposit}
        showToast={showToast}
      />

      {/* ========== REFEREE BONUS SECTION - COMPLETELY INDEPENDENT ========== */}
      <RefereeBonusEditor
        currentValue={refereeConversionPercent}
        isLoading={isLoading}
        onSubmit={onUpdateReferee}
        showToast={showToast}
      />
    </div>
  );
}

// =============================================================================
// DEPOSIT BONUS EDITOR - COMPLETELY SEPARATE COMPONENT
// =============================================================================

interface DepositBonusEditorProps {
  currentValue: number;
  isLoading: boolean;
  onSubmit?: (value: number) => Promise<void>;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

function DepositBonusEditor({
  currentValue,
  isLoading,
  onSubmit,
  showToast,
}: DepositBonusEditorProps) {
  const [inputValue, setInputValue] = useState(String(currentValue));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setInputValue(String(currentValue));
  }, [currentValue]);

  const handlePresetClick = (preset: number) => {
    setInputValue(String(preset));
  };

  const handleSubmit = async () => {
    const value = parseInt(inputValue, 10);

    if (isNaN(value) || value < 0 || value > 100) {
      showToast('Deposit bonus must be between 0-100', 'error');
      return;
    }

    if (value === currentValue) {
      showToast('No change to save', 'info');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit?.(value);
      showToast(`Deposit bonus updated to ${value}%`, 'success');
    } catch {
      showToast('Failed to update deposit bonus', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const parsedValue = parseInt(inputValue, 10);
  const isValidInput = !isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 100;
  const hasChanged = isValidInput && parsedValue !== currentValue;

  return (
    <div className="space-y-4">
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          Deposit Bonus
        </h3>
        <div className="flex items-center gap-3">
          <InputBar
            value={inputValue}
            onChange={setInputValue}
            type="number"
            max={100}
            suffix="%"
            size="lg"
            width="full"
            placeholder="0"
            disabled={isLoading || isSubmitting}
            className="flex-1"
          />
          <ActionButton
            onClick={handleSubmit}
            isActive={hasChanged}
            isLoading={isSubmitting}
            disabled={isLoading || !isValidInput}
            shape="circle"
            size="md"
          >
            <Check className="w-5 h-5" />
          </ActionButton>
        </div>
      </section>

      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          Quick Select
        </h3>
        <div className="flex flex-wrap gap-2">
          {DEPOSIT_PRESETS.map((preset) => (
            <TogglePill
              key={`deposit-preset-${preset}`}
              label={`${preset}%`}
              isSelected={currentValue === preset}
              onClick={() => handlePresetClick(preset)}
              disabled={isLoading || isSubmitting}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

// =============================================================================
// REFEREE BONUS EDITOR - COMPLETELY SEPARATE COMPONENT
// =============================================================================

interface RefereeBonusEditorProps {
  currentValue: number;
  isLoading: boolean;
  onSubmit?: (value: number) => Promise<void>;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

function RefereeBonusEditor({
  currentValue,
  isLoading,
  onSubmit,
  showToast,
}: RefereeBonusEditorProps) {
  const [inputValue, setInputValue] = useState(String(currentValue));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setInputValue(String(currentValue));
  }, [currentValue]);

  const handlePresetClick = (preset: number) => {
    setInputValue(String(preset));
  };

  const handleSubmit = async () => {
    const value = parseInt(inputValue, 10);

    if (isNaN(value) || value < 0 || value > 100) {
      showToast('Referee bonus must be between 0-100', 'error');
      return;
    }

    if (value === currentValue) {
      showToast('No change to save', 'info');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit?.(value);
      showToast(`Referee bonus updated to ${value}%`, 'success');
    } catch {
      showToast('Failed to update referee bonus', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const parsedValue = parseInt(inputValue, 10);
  const isValidInput = !isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 100;
  const hasChanged = isValidInput && parsedValue !== currentValue;

  return (
    <div className="space-y-4">
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          Referee Conversion Bonus
        </h3>
        <div className="flex items-center gap-3">
          <InputBar
            value={inputValue}
            onChange={setInputValue}
            type="number"
            max={100}
            suffix="%"
            size="lg"
            width="full"
            placeholder="0"
            disabled={isLoading || isSubmitting}
            className="flex-1"
          />
          <ActionButton
            onClick={handleSubmit}
            isActive={hasChanged}
            isLoading={isSubmitting}
            disabled={isLoading || !isValidInput}
            shape="circle"
            size="md"
          >
            <Check className="w-5 h-5" />
          </ActionButton>
        </div>
      </section>

      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">
          Quick Select
        </h3>
        <div className="flex flex-wrap gap-2">
          {REFEREE_PRESETS.map((preset) => (
            <TogglePill
              key={`referee-preset-${preset}`}
              label={`${preset}%`}
              isSelected={currentValue === preset}
              onClick={() => handlePresetClick(preset)}
              disabled={isLoading || isSubmitting}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
